import { createApi } from '@reduxjs/toolkit/query/react';
import { saveAs } from 'file-saver';
import { createBaseQuery, toQueryParams } from 'helpers/api';

export const candidateChecksApi = createApi({
  reducerPath: 'candidateChecksApi',
  baseQuery: createBaseQuery('candidate_checks', { timeout: 200000 }),
  endpoints: builder => ({
    getChecks: builder.query({
      query: ({ type, params }) => `/types/${type}?${toQueryParams(params)}`,
    }),
    getAllCandidateChecks: builder.query({
      query: params => `/admin?${toQueryParams(params)}`,
    }),
    downloadAdminCandidateChecksReport: builder.query({
      queryFn: async ({ queryParams }, api, extraOptions, baseQuery) => {
        const { data: file } = await baseQuery({
          url: `/admin/download?${toQueryParams(queryParams)}`,
          responseHandler: async response => {
            const blob = await response.blob();
            return blob;
          },
          cache: 'no-cache',
        });

        saveAs(file, 'candidate_checks_export.csv'); // filename here
        return { data: null };
      },
    }),
    updateCandidateCheck: builder.mutation({
      query: ({ id, data }) => ({
        url: `/${id}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: ({ result }) => result,
    }),
    createCandidateCheck: builder.mutation({
      query: ({ candidateId, data }) => ({
        url: `/candidate/${candidateId}/`,
        method: 'POST',
        body: data,
      }),
      transformResponse: ({ result }) => result,
    }),
    cancelCandidateCheck: builder.mutation({
      query: id => ({
        url: `/${id}/cancel`,
        method: 'PUT',
      }),
      transformResponse: ({ result }) => result,
    }),
    updateStatus: builder.mutation({
      query: ({ id, data }) => ({
        url: `/${id}/update_status`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: ({ result }) => result,
    }),
    updateCheckSupportUser: builder.mutation({
      query: ({ id, data }) => ({
        url: `/${id}/update_support_user`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: ({ result }) => result,
    }),
    createCheckHistory: builder.mutation({
      query: ({ id, data }) => ({
        url: `/${id}/admin/history`,
        method: 'POST',
        body: data,
      }),
      transformResponse: ({ result }) => result,
    }),
    getInternalCheckHistory: builder.query({
      query: ({ id }) => `/${id}/admin/history`,
    }),
    updateCheckHistory: builder.mutation({
      query: ({ id, check_history_id, data }) => ({
        url: `/${id}/admin/history/${check_history_id}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: ({ result }) => result,
    }),
    deleteCheckHistory: builder.mutation({
      query: ({ id, check_history_id }) => ({
        url: `/${id}/admin/history/${check_history_id}`,
        method: 'DELETE',
      }),
    }),
    restoreCandidateCheck: builder.mutation({
      query: id => ({
        url: `/${id}/restore`,
        method: 'PUT',
      }),
      transformResponse: ({ result }) => result,
    }),
    generateReport: builder.mutation({
      query: ({ id, data }) => ({
        url: `/${id}/report/regenerate`,
        method: 'POST',
        body: data,
      }),
    }),
    generateSubchecksCombinedReport: builder.mutation({
      query: id => ({
        url: `/${id}/sub_checks/combined_report`,
        method: 'POST',
      }),
    }),
    regenerateSubChecks: builder.mutation({
      query: id => ({
        url: `/${id}/sub_checks/regenerate`,
        method: 'POST',
      }),
      transformResponse: ({ result }) => result,
    }),
    sendPartnerWebhook: builder.mutation({
      query: id => ({
        url: `/${id}/webhooks`,
        method: 'POST',
      }),
    }),
    getProviderForm: builder.query({
      queryFn: async ({ check, candidate }, api, extraOptions, baseQuery) => {
        const { data: file } = await baseQuery({
          url: `/${check.id}/provider/form`,
          method: 'GET',
          responseHandler: async response => {
            const blob = await response.blob();
            return blob;
          },
          cache: 'no-cache',
        });
        saveAs(file, `${candidate.name}_${check.type}_form`);
        return { data: null };
      },
    }),
    getCandidateCheckResult: builder.query({
      queryFn: async ({ check, candidate, isFile = true }, api, extraOptions, baseQuery) => {
        const response = await baseQuery({
          url: `/${check.id}/result`,
          method: 'GET',
          responseHandler: async response => {
            if (isFile) {
              const blob = await response.blob();
              return blob;
            } else {
              const jsonResponse = await response.json();
              return jsonResponse.result;
            }
          },
          cache: 'no-cache',
        });

        if (response.error) {
          return {
            error: { status: response.error.status },
          };
        }

        if (isFile) saveAs(response.data, `${candidate.name}_${check.type}`);
        else window.open(response.data, '_blank');

        return { data: null };
      },
    }),
    candidateCheckLogViewResult: builder.mutation({
      query: id => ({
        url: `/${id}/result/log`,
        method: 'POST',
      }),
    }),
    getChecskWidgetCount: builder.query({
      query: () => `/widget/counts`,
    }),
    sendToProvider: builder.mutation({
      query: id => ({
        url: `/${id}/provider`,
        method: 'POST',
      }),
      transformResponse: ({ result }) => result,
    }),
    getRenewals: builder.query({
      query: ({ type, params }) => `/types/${type}/renewals?${toQueryParams(params)}`,
      transformResponse: ({ result }) => result,
    }),
    renewCandidateCheck: builder.mutation({
      query: id => ({
        url: `/${id}/renew`,
        method: 'POST',
      }),
      transformResponse: ({ result }) => result,
    }),
    bulkUpdateCandidateChecks: builder.mutation({
      query: data => ({
        url: '/bulk',
        method: 'PUT',
        body: data,
      }),
      transformResponse: ({ result }) => result,
    }),
    bulkRenewCandidateChecks: builder.mutation({
      query: ids => ({
        url: '/bulk/renew',
        method: 'POST',
        body: { ids },
      }),
      transformResponse: ({ result }) => result,
    }),
    getLastCheckOfTypeForCandidateByToken: builder.query({
      query: ({ token, type }) => `/token/${token}/types/${type}`,
      transformResponse: ({ result }) => result,
    }),
    getAnswers: builder.query({
      query: ({ id }) => `/${id}/answers`,
    }),
  }),
});

export const {
  useLazyGetLastCheckOfTypeForCandidateByTokenQuery,
  useLazyGetAllCandidateChecksQuery,
  useLazyDownloadAdminCandidateChecksReportQuery,
  useLazyGetCandidateCheckResultQuery,
  useCreateCandidateCheckMutation,
  useCancelCandidateCheckMutation,
  useRestoreCandidateCheckMutation,
  useUpdateCandidateCheckMutation,
  useSendToProviderMutation,
  useCandidateCheckLogViewResultMutation,
  useLazyGetChecksQuery,
  useGetChecskWidgetCountQuery,
  useLazyGetProviderFormQuery,
  useGenerateReportMutation,
  useGenerateSubchecksCombinedReportMutation,
  useRegenerateSubChecksMutation,
  useLazyGetRenewalsQuery,
  useGetAnswersQuery,
  useRenewCandidateCheckMutation,
  useBulkUpdateCandidateChecksMutation,
  useBulkRenewCandidateChecksMutation,
  useSendPartnerWebhookMutation,
  useUpdateStatusMutation,
  useUpdateCheckSupportUserMutation,
  useCreateCheckHistoryMutation,
  useLazyGetInternalCheckHistoryQuery,
  useUpdateCheckHistoryMutation,
  useDeleteCheckHistoryMutation,
} = candidateChecksApi;
